import { useQuery } from "@tanstack/react-query";

import { ApiError, apiFetch } from "@/api/client";
import type { DagEdge } from "@/api/dag";
import type { LifecycleStateLabel } from "@/api/projects";

// Run-scoped Flow DAG (flowstack#625). Wire-format mirrors
// ``FlowRunDagResponse`` / ``FlowRunNodeState`` / ``FlowRunSummary``
// in src/flowstack/server/routes/dag.py.
//
// Unlike ``useFlowDagOverlay`` (@/api/dag), which is keyed to the
// FLOW TEMPLATE and shows the most-recent run per node across every
// dispatch ever, this is keyed to ONE run — no other run's stale
// state (in particular no other run's failure) can appear here. The
// cohort is grouped exactly by the persisted ``flow_run_id`` (#638);
// see the backend route's docstring.

/** One fan-out sibling of a node, scoped to THIS flow run (flowstack#1281).
 * Mirrors ``FlowRunChildRow`` in src/flowstack/server/routes/dag.py.
 *
 * Not to be confused with ``ChildRunRow`` (@/api/dag's
 * ``useFlowNodeChildren``), which is keyed to the flow TEMPLATE and so mixes
 * every dispatch of it ever — the cross-run bleed #625 removed from this
 * route. These rows come out of the run's own cohort.
 *
 * ``duration_seconds`` is queue-to-terminal wall time DERIVED from the run
 * row's ``created_at``/``updated_at``, not compute time — so it includes every
 * second the run spent queued. The row ALSO carries a VCM-reported compute
 * window since flowstack#1270; this figure is deliberately not that one,
 * because a lane the reader watches progress down needs a number that is
 * defined for a node which has not started and for every row written before
 * that fix. It is the same number the node table has printed for un-fanned
 * nodes since #625, and it keeps counting while the child is still going. */
export interface FlowRunChildRow {
  run_id: string;
  status: LifecycleStateLabel;
  duration_seconds: number | null;
  updated_at: string;
}

export interface FlowRunNodeState {
  name: string;
  tile: string;
  upstream: string[];
  fanout_kind: "scope" | "match" | null;
  /** ``null`` when this node has no run yet (not reached / never
   * dispatched this instance). */
  status: LifecycleStateLabel | null;
  /** The node's single run id. ``null`` when ``child_count !== 1`` —
   * a fanned node's many siblings don't collapse to one run to link
   * to; drill in via the DAG canvas's macro-collapse node click. */
  run_id: string | null;
  cache_hit: boolean | null;
  updated_at: string | null;
  /** Only meaningful when ``child_count === 1``. */
  duration_seconds: number | null;
  /** 0 = not yet dispatched, 1 = singleton, >1 = fanned-out sibling
   * count in THIS run's cohort. NOTE: an expansion-failure marker run
   * (``expansion_reason === "expansion_failed"``) is NOT counted here —
   * the node produced no dispatchable child, which is the point. */
  child_count: number;
  /** This node's fan-out siblings, oldest-DISPATCHED first (flowstack#1281).
   *
   * Empty below two siblings: a node that ran once is already described by
   * ``run_id`` + ``duration_seconds``, and a one-row list would render "fanned
   * out" identically to "ran once".
   *
   * Truncated for very wide fan-outs — the server caps the rows, never the
   * count, so ``children.length < child_count`` means "showing the first N of
   * child_count", and the unbounded list stays at
   * ``GET /flows/{name}/nodes/{node}/children``.
   *
   * Optional so a fixture (or an API build predating #1281) still typechecks. */
  children?: FlowRunChildRow[];
  /** WHY this node has the ``child_count`` it has (flowstack#663).
   *
   * ``child_count: 0`` alone was ambiguous: a fan-out source that was
   * legitimately empty and a driver that crashed mid-expansion rendered
   * identically, which is what made a dead flow look like a finished one.
   *
   * - ``expanded`` — real child runs exist.
   * - ``no_upstream_items`` — the node WAS reached and its fan-out source
   *   list was empty. Nothing failed.
   * - ``not_expanded`` — the flow stopped before reaching this node.
   * - ``expansion_failed`` — the runner could not expand it; ``run_id``
   *   points at the marker run whose ``/logs`` carry the exception. This does
   *   NOT imply ``child_count === 0``: a node can dispatch some children and
   *   only then hit the fault that stopped the rest.
   *
   * Optional so a fixture (or an API build predating #663) still typechecks. */
  expansion_reason?: ExpansionReason | null;
  /** Human-readable "why" behind ``expansion_reason`` — for
   * ``expansion_failed`` this is the exception type + message. */
  expansion_detail?: string | null;
}

export type ExpansionReason =
  | "expanded"
  | "no_upstream_items"
  | "not_expanded"
  | "expansion_failed";

export type FlowRunSummaryState = "not_started" | "running" | "blocked" | "failed" | "completed";

export interface FlowRunSummary {
  state: FlowRunSummaryState;
  frontier_node: string | null;
  frontier_status: LifecycleStateLabel | null;
  message: string;
}

/** Per-flow-run driver liveness (flowstack#371 Phase 5.5 D2). Mirrors
 * ``FlowRunLivenessBlock`` in src/flowstack/server/routes/liveness.py.
 *
 * - ``live`` — a driver completed a pass within ``stale_after_seconds``.
 * - ``pending_first_pass`` — submitted recently, but no driver has completed
 *   a pass over it yet. NOT evidence that a driver is alive: the backend
 *   seeds ``updated_at`` from ``created_at``, so this is the state that
 *   would otherwise masquerade as ``live``.
 * - ``awaiting_recovery`` — the flow run is still ``running`` but nothing
 *   is advancing it (#679). Its state is durable; nothing has been marked
 *   failed, and a reconciler can resume it.
 * - ``settled`` — terminal; no driver expected. */
export type FlowRunLivenessStatus = "live" | "pending_first_pass" | "awaiting_recovery" | "settled";

export interface FlowRunLiveness {
  flow_run_id: string;
  flow_name: string;
  /** The durable ``flow_runs.status`` — NOT the liveness verdict.
   * ``awaiting_recovery`` runs are still honestly ``running``. */
  flow_run_status: "running" | "succeeded" | "failed";
  status: FlowRunLivenessStatus;
  last_driver_pass_at: string;
  driver_age_seconds: number;
  /** ``false`` when ``last_driver_pass_at`` is still the creation timestamp:
   * nothing has ever driven this run, so the timestamp is not evidence of a
   * live driver. */
  driver_pass_recorded: boolean;
  /** Newest child transition, if any. Informational: a driver polling a
   * 45-minute node is live and writes no transition, so progress age
   * never classifies. */
  last_progress_at: string | null;
  progress_age_seconds: number | null;
  stale_after_seconds: number;
  detail: string;
}

export interface FlowRunDagResponse {
  flow_name: string;
  /** The run_id this was fetched for — may be a child node's run. */
  run_id: string;
  /** The reconstructed cohort's root/entry run. Equal to ``run_id``
   * when the caller is already looking at the flow's own parent run;
   * otherwise the child run's page renders a breadcrumb up to it. */
  parent_run_id: string;
  nodes: FlowRunNodeState[];
  edges: DagEdge[];
  summary: FlowRunSummary;
  /** ``null`` when the run can't be tied to a durable ``flow_runs`` row
   * (a pre-#638 child, or one stamped by the listener-based recorder).
   * Optional so a fixture — or an API build predating #371 D2 — still
   * typechecks. */
  liveness?: FlowRunLiveness | null;
  /** WHICH artefact ``nodes``/``edges`` were projected from (flowstack#1463).
   *
   * - ``compiled_dag`` — the run's own ``flow_runs.compiled_dag``, i.e. the
   *   topology it was submitted against. The answer for every run the
   *   durable path submitted, and the reason a run stays viewable after its
   *   template is renamed, moved between sources or uninstalled.
   * - ``flow_template`` — the on-disk template resolved by name. The
   *   fallback for a run with no durable topology (pre-#638, or no
   *   ``flow_runs`` row); the shape is whatever the template says today,
   *   which may have drifted since the run.
   *
   * Optional so a fixture — or an API build predating #1463 — still
   * typechecks. */
  topology_source?: TopologySource;
}

export type TopologySource = "compiled_dag" | "flow_template";

export function flowRunDagQueryKey(runId: string): readonly unknown[] {
  return ["runs", "flow-dag", runId] as const;
}

/** Summary states worth polling on — anything that could still
 * change. ``completed`` and (permanently) ``not_started`` w/o an
 * engine don't need a live poll, but ``not_started`` is also the
 * state right after ``POST /flows/submit`` accepts before the
 * background dispatch has written its first row, so it stays in the
 * active set. */
const POLLING_STATES: ReadonlySet<FlowRunSummaryState> = new Set([
  "not_started",
  "running",
  "blocked",
]);

const POLL_INTERVAL_MS = 5_000;

/** Decide the next poll interval (or ``false`` to stop) from a query's
 * observable state. Extracted as a pure function so the short-circuit
 * is unit-testable without driving react-query internals.
 *
 * flowstack#637 item 2: the PR #634 r0 fix stopped polling whenever
 * ``status === "error"``, but that flag is ALSO set by a single
 * transient failure (a 5xx/network blip that exhausts the local retry
 * budget) even when ``data`` still holds a prior successful, in-flight-
 * looking result — so a focused, online tab would silently stop
 * polling while the flow was still running server-side. Narrow the
 * short-circuit to a GENUINELY PERMANENT outcome: an error on a query
 * that has NEVER successfully fetched (``dataUpdatedAt === 0`` — e.g. a
 * 404 for a standalone Tile run). An error AFTER a prior success keeps
 * polling off the last-known summary state, so a transient error
 * doesn't kill live polling. */
export function nextRefetchInterval(args: {
  status: "pending" | "error" | "success";
  dataUpdatedAt: number;
  summaryState: FlowRunSummaryState | undefined;
}): number | false {
  const { status, dataUpdatedAt, summaryState } = args;
  if (status === "error" && dataUpdatedAt === 0) return false;
  if (!summaryState) return POLL_INTERVAL_MS;
  return POLLING_STATES.has(summaryState) ? POLL_INTERVAL_MS : false;
}

/** Run-scoped Flow DAG + node table + summary for one run.
 *
 * 404s (rather than an empty/degraded body) when ``runId`` doesn't
 * exist, isn't part of a Flow, or its Flow template can't be found —
 * callers distinguish "not a flow run" (``error.errorCode ===
 * "not_a_flow_run"``) from a genuine load failure to pick the right
 * empty-state copy.
 */
export function useFlowRunDag(runId: string, options?: { enabled?: boolean }) {
  const enabled = (options?.enabled ?? true) && Boolean(runId);
  return useQuery({
    queryKey: flowRunDagQueryKey(runId),
    queryFn: async (): Promise<FlowRunDagResponse> =>
      apiFetch<FlowRunDagResponse>(`/api/runs/${encodeURIComponent(runId)}/flow-dag`),
    enabled,
    staleTime: 4_000,
    // A 404 here is an expected, permanent outcome (standalone Tile
    // run, or a run/flow that's gone) — retrying just delays the
    // empty state.
    retry: (failureCount, error) => {
      if (error instanceof ApiError && error.status === 404) return false;
      return failureCount < 2;
    },
    refetchInterval: (query) =>
      // See ``nextRefetchInterval``: stop only on a genuinely permanent
      // error (never-succeeded 404), not on a transient blip that still
      // holds a prior successful result (flowstack#637 item 2).
      nextRefetchInterval({
        status: query.state.status,
        dataUpdatedAt: query.state.dataUpdatedAt,
        summaryState: query.state.data?.summary.state,
      }),
  });
}
